import { Side, Timestamp } from '@/connect';
import { ifTimestampToDate } from '@/utils/helpers/dates';
import { CellClassParams, CellRendererSelectorResult, ColDef } from 'ag-grid-enterprise';
import Decimal from 'decimal.js';
import { SetupFunction, defineComponent } from 'vue';

// eslint-disable-next-line @typescript-eslint/naming-convention
export const ComponentProxy = defineComponent<{
  params: {
    component: {
      props: string[];
      setup: SetupFunction<Record<string, unknown>>;
    };
    props: Record<string, unknown>;
  };
}>({
  render(h) {
    const cmp = defineComponent(this.params.component);
    return h(cmp, { props: this.params.props });
  },
});

export function component<Props extends Record<string, unknown>>(
  setup: SetupFunction<Props>,
  components?: Record<string, unknown>
) {
  return (props: Props | undefined): undefined | CellRendererSelectorResult => {
    return !props
      ? undefined
      : {
          component: 'ComponentProxy',
          params: {
            props,
            component: {
              props: Object.fromEntries(
                Object.keys(props).map((key) => [key, { default: undefined }])
              ),
              setup,
              components,
            },
          },
        };
  };
}

export function alignLeft(): Pick<ColDef, 'cellClass' | 'suppressHeaderMenuButton'> {
  return {
    cellClass(params) {
      const extraPadding = params.colDef.suppressHeaderMenuButton ? 0 : 4;
      return `align-center justify-start pl-${extraPadding + 4}`;
    },
  };
}

export function alignRight(config?: {
  hasPostfix: boolean;
}): Pick<ColDef, 'headerClass' | 'cellClass' | 'suppressHeaderMenuButton'> {
  return {
    headerClass(params) {
      const extraPadding = (params as CellClassParams<unknown, unknown>).colDef
        .suppressHeaderMenuButton
        ? 0
        : 4;
      return `header-right pr-${extraPadding + 6}`;
    },
    cellClass(params) {
      const extraPadding = params.colDef.suppressHeaderMenuButton ? 0 : 4;
      return `align-center justify-end pr-${extraPadding + (config?.hasPostfix ? 4 : 6)}`;
    },
  };
}

export function alignCenter(): Pick<ColDef, 'headerClass' | 'cellClass'> {
  return {
    headerClass: 'header-center',
    cellClass: 'align-center justify-center',
  };
}

function cellClassToString<Params>(
  cellClass:
    | undefined
    | string
    | string[]
    | ((params: Params) => null | undefined | string | string[]),
  params: Params
) {
  const result = typeof cellClass === 'function' ? cellClass(params) : cellClass;
  return Array.isArray(result) ? result.join(' ') : (result ?? '');
}

export function mergeClasses<TData, TValue = unknown>(
  classes: Array<Pick<ColDef<TData, TValue>, 'headerClass' | 'cellClass'>>
): Pick<ColDef<TData, TValue>, 'headerClass' | 'cellClass'> {
  return {
    headerClass: (params) =>
      classes.map(({ headerClass }) => cellClassToString(headerClass, params)),
    cellClass: (params) => classes.map(({ cellClass }) => cellClassToString(cellClass, params)),
  };
}

export function alignColumn(config: {
  align: 'left' | 'right' | 'center';
  hasPostfix?: boolean;
}): Pick<ColDef, 'headerClass' | 'cellClass'> {
  config.hasPostfix = config.hasPostfix ?? false;
  switch (config.align) {
    case 'left':
      return alignLeft();
    case 'right':
      return alignRight({ hasPostfix: config.hasPostfix });
    case 'center':
      return alignCenter();
  }
}

export const comparator = {
  date(
    a: Date | number | string | null | Timestamp | undefined,
    b: Date | number | string | null | Timestamp | undefined
  ) {
    a = ifTimestampToDate(a);
    b = ifTimestampToDate(b);
    a = +new Date(a ?? NaN);
    b = +new Date(b ?? NaN);
    return comparator.number(isNaN(a) ? Infinity : a, isNaN(b) ? Infinity : b);
  },
  timestamp(a: Timestamp | null | undefined, b: Timestamp | null | undefined) {
    return comparator.date(ifTimestampToDate(a), ifTimestampToDate(b));
  },
  number(a: number | null | undefined, b: number | null | undefined) {
    a ??= Infinity;
    b ??= Infinity;
    return a - b;
  },
  bigint(a: bigint | string | null | undefined, b: bigint | string | null | undefined) {
    a ??= BigInt(Infinity);
    b ??= BigInt(Infinity);
    a = typeof a === 'bigint' ? a : BigInt(a);
    b = typeof b === 'bigint' ? b : BigInt(b);
    return a > b ? 1 : a < b ? -1 : 0;
  },
  decimal(
    a: Decimal | number | string | null | undefined,
    b: Decimal | number | string | null | undefined
  ) {
    a ??= new Decimal(Infinity);
    b ??= new Decimal(Infinity);

    // replacing all non-numeric characters with empty string
    if (typeof a === 'string') a = a.replaceAll(/[^0-9\-.]/g, '');
    if (typeof b === 'string') b = b.replaceAll(/[^0-9\-.]/g, '');

    a = a instanceof Decimal ? a : new Decimal(a);
    b = b instanceof Decimal ? b : new Decimal(b);
    return a.comparedTo(b);
  },
} as const;

export const equals = {
  decimal(a: Decimal | null | undefined, b: Decimal | null | undefined): boolean {
    return a && b ? a.equals(b) : a === b;
  },
  array<T>(valueA: T[], valueB: T[], areEqual = (a: T, b: T) => a === b) {
    return (
      valueA === valueB ||
      (valueA.length === valueB.length && valueA.every((v, i) => areEqual(v, valueB[i])))
    );
  },
} as const;

export function formatSide(side: Side): string {
  return side === Side.BORROW ? 'BORROW' : 'LOAN';
}

export function sortNormalizedString(
  valueA: string | null | undefined,
  valueB: string | null | undefined
): number {
  if (valueA == null) return -1;
  if (valueB == null) return 1;
  return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
}
